import React, { useRef, useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import AutocompleteInput from './AutocompleteInput';
import { makeEmptyRow, coerceDuration, tsvToRows, rowsToTsv, rowDuplicateKey, isRowEmpty } from '../utils/serviceRows';
import { DURATIONS, GENDERS } from '../constants/masterData';
import './ServiceEntryGrid.css';

/**
 * Spreadsheet-style entry grid for salon services.
 * Fully controlled: the page owns `rows`; the grid owns only ephemeral UI
 * state (focus, selection). Exposes focusCell(rowId, field) via ref so the
 * validation panel can jump to an offending cell.
 */
const ServiceEntryGrid = forwardRef(function ServiceEntryGrid(
  { rows, onRowsChange, masterServices, masterCategories, errorFields, onAddToMaster },
  ref
) {
  const cellRefs = useRef({});
  const pendingFocus = useRef(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [copyFeedback, setCopyFeedback] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const enabledCategories = useMemo(
    () => (masterCategories || []).filter((c) => c.enabled),
    [masterCategories]
  );

  // Categories the salon/admin typed in that aren't part of the master
  // catalog — collected from whatever rows already use them so every row's
  // dropdown can offer them, not just the row that first introduced it.
  const customCategories = useMemo(() => {
    const masterNames = new Set(enabledCategories.map((c) => c.name));
    const seen = new Set();
    const result = [];
    rows.forEach((r) => {
      const name = (r.category || '').trim();
      if (name && !masterNames.has(name) && !seen.has(name)) {
        seen.add(name);
        result.push(name);
      }
    });
    return result;
  }, [rows, enabledCategories]);

  const promptForNewCategory = () => {
    const name = window.prompt('New category name:');
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const existing = [...enabledCategories.map((c) => c.name), ...customCategories].find(
      (n) => n.toLowerCase() === trimmed.toLowerCase()
    );
    return existing || trimmed;
  };

  // Search across already-entered rows, to recheck whether a service is
  // available before adding it again
  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) =>
        [(row.service || ''), (row.subService || ''), (row.category || '')].some((v) =>
          v.toLowerCase().includes(q)
        )
      );
  }, [rows, searchQuery]);

  const searchMatchIds = useMemo(
    () => new Set(searchMatches.map(({ row }) => row.id)),
    [searchMatches]
  );
  const enabledServices = useMemo(
    () => (masterServices || []).filter((s) => s.enabled),
    [masterServices]
  );

  const servicesByCategory = useMemo(() => {
    const map = new Map();
    enabledServices.forEach((s) => {
      const key = (s.categoryName || '').trim().toLowerCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return map;
  }, [enabledServices]);

  // When the row already has a category, autocomplete only suggests that
  // category's services; without a category it searches the whole catalog
  const optionsForRow = (row) => {
    const key = (row.category || '').trim().toLowerCase();
    if (!key) return enabledServices;
    return servicesByCategory.get(key) || [];
  };

  useEffect(() => {
    if (!pendingFocus.current) return;
    const { rowId, col } = pendingFocus.current;
    const el = cellRefs.current[`${rowId}:${col}`];
    if (el) {
      el.focus();
      if (typeof el.select === 'function') el.select();
    }
    pendingFocus.current = null;
  }, [rows]);

  const registerCell = (rowId, col) => (el) => {
    cellRefs.current[`${rowId}:${col}`] = el;
  };

  useImperativeHandle(ref, () => ({
    focusCell(rowId, col) {
      const el = cellRefs.current[`${rowId}:${col}`];
      if (el) {
        el.focus();
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }));

  const updateRow = (rowId, patch) => {
    onRowsChange(rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));
  };

  const insertRowsAt = (index, newRows, focusCol = 'service') => {
    const next = [...rows.slice(0, index), ...newRows, ...rows.slice(index)];
    pendingFocus.current = { rowId: newRows[0].id, col: focusCol };
    onRowsChange(next);
  };

  const addRowBelow = (rowIndex) => {
    const current = rows[rowIndex];
    const newRow = makeEmptyRow({
      category: current?.category || '',
      gender: current?.gender,
      duration: current?.duration
    });
    insertRowsAt(rowIndex + 1, [newRow]);
  };

  const deleteRow = (rowIndex) => {
    const row = rows[rowIndex];
    // Blank rows are removed silently; anything with content needs confirmation
    if (!isRowEmpty(row)) {
      const label = (row.service || '').trim()
        ? `"${row.service.trim()}${(row.subService || '').trim() ? ` - ${row.subService.trim()}` : ''}"`
        : 'this row';
      if (!window.confirm(`Remove ${label} from this salon's service list?`)) return;
    }
    const next = rows.filter((r) => r.id !== row.id);
    const focusRow = next[Math.max(0, Math.min(rowIndex - 1, next.length - 1))];
    if (focusRow) pendingFocus.current = { rowId: focusRow.id, col: 'service' };
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      copy.delete(row.id);
      return copy;
    });
    onRowsChange(next);
  };

  const deleteSelected = () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected row(s)?`)) return;
    setSelectedIds(new Set());
    onRowsChange(rows.filter((r) => !selectedIds.has(r.id)));
  };

  // Start (or extend) a category section with one blank row, category preselected
  const addServiceToCategory = (categoryName) => {
    const sectionRows = rows.filter((r) => (r.category || '').trim() === categoryName);
    const last = sectionRows[sectionRows.length - 1];
    const newRow = makeEmptyRow({
      category: categoryName,
      gender: last?.gender,
      duration: last?.duration
    });
    // Appending is enough — the page re-groups rows into their category section
    pendingFocus.current = { rowId: newRow.id, col: 'service' };
    onRowsChange([...rows, newRow]);
  };

  const copySelected = async () => {
    const selectedRows = rows.filter((r) => selectedIds.has(r.id));
    if (!selectedRows.length) return;
    try {
      await navigator.clipboard.writeText(rowsToTsv(selectedRows));
      setCopyFeedback(`Copied ${selectedRows.length} row(s)`);
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      alert('Could not access the clipboard. Please allow clipboard permission.');
    }
  };

  const toggleSelected = (rowId) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(rowId)) copy.delete(rowId);
      else copy.add(rowId);
      return copy;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  };

  const handleMasterSelect = (rowIndex, master) => {
    const row = rows[rowIndex];
    // Keys of already-entered rows, to block adding the same service twice
    const existingKeys = new Map();
    rows.forEach((r, i) => {
      if (r.id !== row.id && (r.service || '').trim()) existingKeys.set(rowDuplicateKey(r), i);
    });

    if (master.subServices && master.subServices.length) {
      // Expand into one pre-filled row per subservice; admin just types prices.
      // Subservices already in the grid are skipped instead of duplicated.
      const missingSubs = master.subServices.filter(
        (sub) =>
          !existingKeys.has(
            rowDuplicateKey({
              category: master.categoryName,
              service: master.name,
              subService: sub.name,
              gender: master.gender
            })
          )
      );
      if (!missingSubs.length) {
        alert(`"${master.name}" and all its sub services are already added for this salon.`);
        updateRow(row.id, { service: '' });
        return;
      }
      const subRows = missingSubs.map((sub) =>
        makeEmptyRow({
          category: master.categoryName,
          service: master.name,
          subService: sub.name,
          gender: master.gender,
          duration: coerceDuration(sub.defaultDuration || master.defaultDuration),
          masterServiceId: master.id
        })
      );
      const next = [...rows.slice(0, rowIndex), ...subRows, ...rows.slice(rowIndex + 1)];
      pendingFocus.current = { rowId: subRows[0].id, col: 'price' };
      onRowsChange(next);
    } else {
      const dupIndex = existingKeys.get(
        rowDuplicateKey({
          category: master.categoryName,
          service: master.name,
          subService: '',
          gender: master.gender
        })
      );
      if (dupIndex !== undefined) {
        alert(`"${master.name}" is already added for this salon (row ${dupIndex + 1}).`);
        updateRow(row.id, { service: '' });
        pendingFocus.current = { rowId: rows[dupIndex].id, col: 'price' };
        return;
      }
      updateRow(row.id, {
        service: master.name,
        category: master.categoryName,
        gender: master.gender,
        duration: coerceDuration(master.defaultDuration),
        masterServiceId: master.id
      });
      pendingFocus.current = { rowId: row.id, col: 'price' };
    }
  };

  const handleCellKeyDown = (e, rowIndex, col) => {
    const isSelect = e.target.tagName === 'SELECT';

    if (e.key === 'Enter') {
      e.preventDefault();
      addRowBelow(rowIndex);
      return;
    }

    if (e.key === 'Backspace' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      deleteRow(rowIndex);
      return;
    }

    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !isSelect) {
      const targetIndex = e.key === 'ArrowDown' ? rowIndex + 1 : rowIndex - 1;
      if (targetIndex >= 0 && targetIndex < rows.length) {
        e.preventDefault();
        const el = cellRefs.current[`${rows[targetIndex].id}:${col}`];
        if (el) {
          el.focus();
          if (typeof el.select === 'function') el.select();
        }
      }
      return;
    }

    // Tab off the last cell of the last row appends a new row (spreadsheet feel)
    if (e.key === 'Tab' && !e.shiftKey && col === 'price' && rowIndex === rows.length - 1) {
      e.preventDefault();
      addRowBelow(rowIndex);
    }
  };

  const handlePaste = (e, rowIndex) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!text.includes('\t') && !text.includes('\n')) return; // plain text — let the input handle it
    e.preventDefault();
    const pastedRows = tsvToRows(text);
    if (pastedRows.length) {
      insertRowsAt(rowIndex + 1, pastedRows, 'price');
    }
  };

  const errorClass = (rowId, field) =>
    errorFields?.[rowId]?.has(field) ? 'grid-cell-error' : '';

  return (
    <div className="service-grid">
      <div className="grid-toolbar">
        <div className="grid-toolbar-left">
          <select
            className="grid-add-category-select"
            value=""
            onChange={(e) => {
              const val = e.target.value;
              if (!val) return;
              if (val === '__new__') {
                const name = promptForNewCategory();
                if (name) addServiceToCategory(name);
                return;
              }
              addServiceToCategory(val);
            }}
          >
            <option value="" disabled>＋ Add Category</option>
            <option value="__new__">＋ New category...</option>
            {customCategories.map((name) => (
              <option key={name} value={name}>{name} ✓</option>
            ))}
            {enabledCategories.map((c) => {
              const inGrid = rows.some((r) => (r.category || '').trim() === c.name);
              return (
                <option key={c.id} value={c.name}>
                  {c.name}{inGrid ? ' ✓' : ''}
                </option>
              );
            })}
          </select>
          <button className="grid-toolbar-btn" onClick={copySelected} disabled={!selectedIds.size}>
            Copy {selectedIds.size || ''} row{selectedIds.size === 1 ? '' : 's'}
          </button>
          <button className="grid-toolbar-btn danger" onClick={deleteSelected} disabled={!selectedIds.size}>
            Delete selected
          </button>
          {copyFeedback && <span className="grid-copy-feedback">{copyFeedback}</span>}
        </div>
        <div className="grid-toolbar-right">
          <div className="grid-search-wrapper">
            <input
              type="text"
              className="grid-search-input"
              placeholder="🔍 Check if a service is added..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="grid-search-clear" onClick={() => setSearchQuery('')} title="Clear search">
                ×
              </button>
            )}
          </div>
          <span className="grid-row-count">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      {searchQuery.trim() && (
        <div className={`grid-search-results ${searchMatches.length ? 'has-matches' : 'no-matches'}`}>
          {searchMatches.length ? (
            <>
              <span className="grid-search-summary">
                ✓ Already added — {searchMatches.length} match{searchMatches.length === 1 ? '' : 'es'} (highlighted below):
              </span>
              {searchMatches.slice(0, 6).map(({ row, index }) => (
                <button
                  key={row.id}
                  className="grid-search-match-link"
                  onClick={() => {
                    const el = cellRefs.current[`${row.id}:service`];
                    if (el) {
                      el.focus();
                      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    }
                  }}
                >
                  Row {index + 1}: {row.category || 'Uncategorized'} › {row.service || '(no name)'}
                  {(row.subService || '').trim() ? ` - ${row.subService}` : ''} · {row.gender}
                  {Number(row.price) > 0 ? ` · ₹${row.price}` : ''}
                </button>
              ))}
              {searchMatches.length > 6 && (
                <span className="grid-search-more">…and {searchMatches.length - 6} more</span>
              )}
            </>
          ) : (
            <span className="grid-search-summary">
              Not added yet — "{searchQuery.trim()}" is not in this salon's list. Safe to add it.
            </span>
          )}
        </div>
      )}

      <div className="grid-table-wrapper">
        <table className="grid-table">
          <thead>
            <tr>
              <th className="grid-col-check">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selectedIds.size === rows.length}
                  onChange={toggleSelectAll}
                  title="Select all"
                />
              </th>
              <th className="grid-col-num">#</th>
              <th className="grid-col-category">Category</th>
              <th className="grid-col-service">Service</th>
              <th className="grid-col-sub">Sub Service</th>
              <th className="grid-col-gender">Gender</th>
              <th className="grid-col-duration">Duration</th>
              <th className="grid-col-price">Price (₹)</th>
              <th className="grid-col-actions"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr className="grid-empty-row">
                <td colSpan={9}>
                  No services yet — use <strong>＋ Add Category</strong> above to start a section.
                </td>
              </tr>
            )}
            {rows.map((row, rowIndex) => {
              // Rows arrive grouped by category (page sorts them), so a change
              // in category between consecutive rows starts a new section
              const prevRow = rows[rowIndex - 1];
              const category = (row.category || '').trim();
              const showHeader = !prevRow || (prevRow.category || '').trim() !== category;
              const groupCount = showHeader
                ? rows.filter((r) => (r.category || '').trim() === category).length
                : 0;
              return (
              <React.Fragment key={row.id}>
              {showHeader && (
                <tr className="grid-category-header-row">
                  <td colSpan={9}>
                    <div className="grid-category-header-inner">
                      <span className="grid-category-header-name">
                        {category || 'Uncategorized'}
                      </span>
                      <span className="grid-category-header-count">
                        {groupCount} row{groupCount === 1 ? '' : 's'}
                      </span>
                      <button
                        className="grid-add-service-btn"
                        onClick={() => addServiceToCategory(category)}
                        tabIndex={-1}
                      >
                        + Add Service
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              <tr
                className={[
                  selectedIds.has(row.id) ? 'grid-row-selected' : '',
                  searchMatchIds.has(row.id) ? 'grid-row-search-match' : ''
                ].join(' ').trim()}
              >
                <td className="grid-col-check">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(row.id)}
                    onChange={() => toggleSelected(row.id)}
                    tabIndex={-1}
                  />
                </td>
                <td className="grid-col-num">{rowIndex + 1}</td>
                <td className={errorClass(row.id, 'category')}>
                  <select
                    ref={registerCell(row.id, 'category')}
                    className="grid-input"
                    value={row.category}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Changing category moves the row into its category
                      // section; re-focus the select after the move
                      if (val === '__new__') {
                        const name = promptForNewCategory();
                        if (!name) return;
                        pendingFocus.current = { rowId: row.id, col: 'category' };
                        updateRow(row.id, { category: name });
                        return;
                      }
                      pendingFocus.current = { rowId: row.id, col: 'category' };
                      updateRow(row.id, { category: val });
                    }}
                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'category')}
                  >
                    <option value="">Select...</option>
                    <option value="__new__">＋ New category...</option>
                    {row.category && !enabledCategories.some((c) => c.name === row.category) && !customCategories.includes(row.category) && (
                      <option value={row.category}>{row.category}</option>
                    )}
                    {customCategories.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                    {enabledCategories.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </td>
                <td className={errorClass(row.id, 'service')}>
                  <AutocompleteInput
                    ref={registerCell(row.id, 'service')}
                    className="grid-input"
                    value={row.service}
                    placeholder="Type a service..."
                    options={optionsForRow(row)}
                    onChange={(text) => updateRow(row.id, { service: text, masterServiceId: '' })}
                    onSelect={(master) => handleMasterSelect(rowIndex, master)}
                    onCreateNew={onAddToMaster ? (text) => onAddToMaster(text, row.id) : undefined}
                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'service')}
                    onPaste={(e) => handlePaste(e, rowIndex)}
                  />
                </td>
                <td className={errorClass(row.id, 'subService')}>
                  <input
                    ref={registerCell(row.id, 'subService')}
                    type="text"
                    className="grid-input"
                    value={row.subService}
                    placeholder="—"
                    onChange={(e) => updateRow(row.id, { subService: e.target.value })}
                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'subService')}
                    onPaste={(e) => handlePaste(e, rowIndex)}
                  />
                </td>
                <td className={errorClass(row.id, 'gender')}>
                  <select
                    ref={registerCell(row.id, 'gender')}
                    className="grid-input"
                    value={row.gender}
                    onChange={(e) => updateRow(row.id, { gender: e.target.value })}
                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'gender')}
                  >
                    {GENDERS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </td>
                <td className={errorClass(row.id, 'duration')}>
                  <select
                    ref={registerCell(row.id, 'duration')}
                    className="grid-input"
                    value={row.duration}
                    onChange={(e) => updateRow(row.id, { duration: Number(e.target.value) })}
                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'duration')}
                  >
                    {DURATIONS.map((d) => (
                      <option key={d} value={d}>{d} mins</option>
                    ))}
                  </select>
                </td>
                <td className={errorClass(row.id, 'price')}>
                  <input
                    ref={registerCell(row.id, 'price')}
                    type="number"
                    min="0"
                    className="grid-input grid-price-input"
                    value={row.price ?? ''}
                    placeholder="0"
                    onChange={(e) =>
                      updateRow(row.id, { price: e.target.value === '' ? null : Number(e.target.value) })
                    }
                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'price')}
                    onPaste={(e) => handlePaste(e, rowIndex)}
                  />
                </td>
                <td className="grid-col-actions">
                  <button
                    className="grid-delete-btn"
                    onClick={() => deleteRow(rowIndex)}
                    tabIndex={-1}
                    title="Delete row (Ctrl+Backspace)"
                  >
                    ×
                  </button>
                </td>
              </tr>
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid-hints">
        <span><kbd>Enter</kbd> new row</span>
        <span><kbd>Tab</kbd> next field</span>
        <span><kbd>↑</kbd><kbd>↓</kbd> move between rows</span>
        <span><kbd>Ctrl</kbd>+<kbd>⌫</kbd> delete row</span>
      </div>
    </div>
  );
});

export default ServiceEntryGrid;
